import { useState, useEffect } from "react";
import { LandPlot, Ruler, Hand, ShoppingCart, BookOpen, Package } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Lucide equipment icons in HG green (audit item #10 — no emoji).
const EQUIPMENT_ICONS = {
  demo_kit: LandPlot,
  lie_board: Ruler,
  grip_samples: Hand,
  club_cart: ShoppingCart,
  fitters_manual: BookOpen,
};

const COMPONENT_TYPES = [
  { id: "demo_clubs", name: "Demo Clubs", description: "Replacement or additional demo clubs" },
  { id: "lie_board", name: "Lie Board", description: "Patented lie board for fitting" },
  { id: "grip_samples", name: "Grip Samples", description: "Sample grip sizes" },
  { id: "cart", name: "Club Cart", description: "Cart for holding demo clubs" },
  { id: "manual", name: "Fitter's Manual", description: "Training and procedure manual" },
];

export function FittingSystemView({ user }) {
  const [system, setSystem] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRequestForm, setShowRequestForm] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    try {
      const [systemRes, requestsRes] = await Promise.all([
        fetch(`${API_URL}/api/fitting-system`, { credentials: "include" }),
        fetch(`${API_URL}/api/fitting-system/requests`, { credentials: "include" })
      ]);
      
      if (systemRes.ok) setSystem(await systemRes.json());
      if (requestsRes.ok) {
        const data = await requestsRes.json();
        setRequests(data.requests || []);
      }
    } catch (err) {
      console.error("Failed to load fitting system:", err);
    }
    setLoading(false);
  }

  function getStatusColor(status) {
    const colors = {
      active: "#48B64C",
      pending: "#f59e0b",
      approved: "#3b82f6",
      shipped: "#8b5cf6",
      delivered: "#10b981",
      denied: "#ef4444"
    };
    return colors[status] || "#666";
  }

  if (loading) {
    return (
      <div className="content" data-testid="fitting-system">
        <div className="section-title">Fitting System</div>
        <div className="loading-state">Loading your fitting system...</div>
      </div>
    );
  }

  if (showRequestForm) {
    return (
      <RequestComponentForm 
        onClose={() => setShowRequestForm(false)}
        onSuccess={() => { setShowRequestForm(false); loadData(); }}
      />
    );
  }

  return (
    <div className="content fitting-system-page" data-testid="fitting-system">
      <div className="section-title">Fitting System</div>
      <p className="section-sub">View your fitting equipment and request additional components.</p>

      {/* Current Equipment */}
      <div className="admin-card">
        <h3>Your Equipment</h3>
        <div className="equipment-grid">
          {system?.components && Object.entries(system.components).map(([key, item]) => (
            <div key={key} className="equipment-card" data-testid={`equipment-${key}`}>
              <div className="equipment-icon">
                {(() => { const I = EQUIPMENT_ICONS[key] || Package; return <I size={26} strokeWidth={1.6} />; })()}
              </div>
              <div className="equipment-info">
                <h4>{item.name}</h4>
                <p>{item.description}</p>
                <span 
                  className="equipment-status"
                  style={{ color: getStatusColor(item.status) }}
                >
                  ● {item.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Request Component Button */}
      <div className="section-actions">
        <button 
          className="btn btn-primary"
          onClick={() => setShowRequestForm(true)}
          data-testid="request-component-btn"
        >
          + Request Component
        </button>
      </div>

      {/* Pending Requests */}
      {requests.length > 0 && (
        <div className="admin-card">
          <h3>Your Requests</h3>
          <div className="requests-list">
            {requests.map(req => (
              <div key={req.request_id} className="request-item" data-testid={`request-${req.request_id}`}>
                <div className="request-info">
                  <span className="request-type">
                    {COMPONENT_TYPES.find(c => c.id === req.component_type)?.name || req.component_type}
                  </span>
                  <span className="request-date">
                    {new Date(req.created_at).toLocaleDateString()}
                  </span>
                </div>
                <span 
                  className="request-status"
                  style={{ 
                    background: getStatusColor(req.status) + "20",
                    color: getStatusColor(req.status)
                  }}
                >
                  {req.status}
                </span>
                {req.tracking_number && (
                  <span className="tracking-number">Tracking: {req.tracking_number}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Help Info */}
      <div className="admin-card help-card">
        <h3>Need Help?</h3>
        <p>
          If you have questions about your fitting system or need assistance, 
          please contact Henry-Griffitts support at{" "}
          <a href="mailto:hgsupport@henry-griffitts.com">hgsupport@henry-griffitts.com</a>
        </p>
      </div>
    </div>
  );
}

function RequestComponentForm({ onClose, onSuccess }) {
  const [componentType, setComponentType] = useState("demo_clubs");
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState("");
  const [urgency, setUrgency] = useState("normal");
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const res = await fetch(`${API_URL}/api/fitting-system/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          component_type: componentType,
          quantity,
          notes,
          urgency
        })
      });
      
      if (res.ok) {
        onSuccess();
      }
    } catch (err) {
      console.error("Failed to submit request:", err);
    }
    setSubmitting(false);
  }

  return (
    <div className="content request-form" data-testid="request-component-form">
      <div className="section-title">Request Component</div>
      <button className="btn btn-secondary back-btn" onClick={onClose}>← Back</button>

      <form onSubmit={handleSubmit} className="admin-card">
        <div className="form-field">
          <label>Component Type</label>
          <select 
            value={componentType} 
            onChange={(e) => setComponentType(e.target.value)}
            data-testid="component-type"
          >
            {COMPONENT_TYPES.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <span className="field-hint">
            {COMPONENT_TYPES.find(c => c.id === componentType)?.description}
          </span>
        </div>

        <div className="form-field">
          <label>Quantity</label>
          <input
            type="number"
            min="1"
            max="10"
            value={quantity}
            onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
            data-testid="quantity"
          />
        </div>

        <div className="form-field">
          <label>Urgency</label>
          <select 
            value={urgency} 
            onChange={(e) => setUrgency(e.target.value)}
            data-testid="urgency"
          >
            <option value="normal">Normal</option>
            <option value="urgent">Urgent (expedited shipping)</option>
          </select>
        </div>

        <div className="form-field">
          <label>Notes (optional)</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any additional details about your request..."
            rows={3}
            data-testid="notes"
          />
        </div>

        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={submitting}
            data-testid="submit-request-btn"
          >
            {submitting ? "Submitting..." : "Submit Request"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default FittingSystemView;
